import { useEffect } from 'react';

/**
 * GPT's out-of-page interstitial wrapper: `gpt_unit_/<network>/<unit path>/interstitial_<n>`.
 * Only the `gpt_unit_` prefix is GPT's own — everything after it is the tenant's network code,
 * ad unit path and domain, so the prefix match is what makes this work on every tenant.
 *
 * The slot is injected by the raw `googletag.defineOutOfPageSlot(...)` script in the tenant's
 * `index-with-ads.html` template, not by React, so it can only be reached through the DOM.
 */
const SLOT_SELECTOR = 'ins[id^="gpt_unit_"]';

/** The same-origin "friendly" iframe GPT renders the creative into, inside that wrapper. */
const CREATIVE_IFRAME_SELECTOR = 'iframe[id^="google_ads_iframe_"]';

/**
 * How long a slot with no creative frame at all has to stay quiet before it is called unfilled.
 * That shape is unambiguous — there is nothing on its way to painting.
 */
export const EMPTY_SLOT_GRACE_MS = 400;

/**
 * The same window for a slot whose creative frame is present but blank, which gets considerably
 * longer. GPT only unparks the interstitial once it believes it has something to show, so a blank
 * body here is usually genuinely unfilled — but a slow creative on a busy main thread looks
 * identical for a moment, and removal is one-way. A second more of an already-blank overlay costs
 * the visitor very little; deleting an ad that was about to paint cannot be undone.
 */
export const BLANK_CREATIVE_GRACE_MS = 1500;

/**
 * The ceiling on that debounce. A slot the vendor restyles more often than the grace window
 * would otherwise postpone the verdict forever — silently never firing, which is exactly the
 * failure this guard exists to prevent.
 */
export const EMPTY_SLOT_MAX_WAIT_MS = 3000;

function readCreativeBody(iframe: HTMLIFrameElement): HTMLElement | null {
  try {
    return iframe.contentDocument?.body ?? null;
  } catch {
    return null;
  }
}

const creativeFrames = (slot: HTMLElement) => slot.querySelectorAll<HTMLIFrameElement>(CREATIVE_IFRAME_SELECTOR);

/**
 * Whether one creative frame is rendering nothing — a document body with no elements and no text
 * (a body holding only a comment still renders nothing).
 *
 * A body we cannot read is deliberately NOT blank: a cross-origin creative throws on
 * `contentDocument`, and a frame with no document yet returns null. Neither proves the frame is
 * unfilled, and guessing wrong there would drop a real ad.
 */
function isCreativeBlank(iframe: HTMLIFrameElement): boolean {
  const body = readCreativeBody(iframe);
  // `textContent` is only null for document and doctype nodes, never for an element.
  return !!body && body.children.length === 0 && body.textContent!.trim() === '';
}

/**
 * Whether the slot is covering the page while rendering nothing.
 *
 * Every creative frame has to be blank, not just the first: a refresh can insert the new frame
 * before unlinking the emptied old one, and judging on `querySelector` alone would then delete a
 * correctly-rendering, paying interstitial. A slot with no frames at all vacuously qualifies,
 * which is the right answer for one that never rendered a creative.
 */
const isSlotEmpty = (slot: HTMLElement) => Array.from(creativeFrames(slot)).every(isCreativeBlank);

/** How long this slot's shape earns before a verdict. */
const graceFor = (slot: HTMLElement) =>
  creativeFrames(slot).length > 0 ? BLANK_CREATIVE_GRACE_MS : EMPTY_SLOT_GRACE_MS;

/**
 * Whether the slot is on screen right now, as opposed to armed and parked.
 *
 * GPT creates the `<ins>` as soon as `googletag.display(slot)` runs and leaves it sitting at
 * `display: none !important` — often for the whole page view — until it has a creative to show.
 * A parked slot covers nothing and blocks nothing, and dropping one would destroy the
 * interstitial before it ever had a chance to fill. Only a slot that is actually painting over
 * the page is this guard's business.
 */
function isSlotRendered(slot: HTMLElement): boolean {
  const { display, visibility } = getComputedStyle(slot);
  return display !== 'none' && visibility !== 'hidden';
}

interface CreativeWatch {
  document: Document;
  observer: MutationObserver;
}

interface SlotState {
  observers: MutationObserver[];
  /** At most one live document observer per creative frame — see `watchCreativeDocument`. */
  creativeWatches: Map<HTMLIFrameElement, CreativeWatch>;
  listeners: Array<[HTMLIFrameElement, () => void]>;
  timer: ReturnType<typeof setTimeout> | null;
  waitingSince: number | null;
  watchedIframes: WeakSet<HTMLIFrameElement>;
}

const createSlotState = (): SlotState => ({
  observers: [],
  creativeWatches: new Map(),
  listeners: [],
  timer: null,
  waitingSince: null,
  watchedIframes: new WeakSet(),
});

function releaseSlot(state: SlotState) {
  state.observers.forEach((observer) => observer.disconnect());
  state.creativeWatches.forEach(({ observer }) => observer.disconnect());
  state.listeners.forEach(([iframe, onLoad]) => iframe.removeEventListener('load', onLoad));
  if (state.timer !== null) {
    clearTimeout(state.timer);
  }
}

/**
 * Watching the creative's own document is what catches a creative that arrives with no further
 * `load` event — the only other signal we would get. It is same-origin, so this is readable.
 *
 * A `load` replaces the frame's document, so the observer is keyed by frame and the superseded
 * one is disconnected rather than left attached to a detached body. Appending each new observer
 * to a shared list instead would retain every discarded creative DOM for as long as the slot is
 * guarded — and a filled slot is never released before navigation.
 */
function watchCreativeDocument(state: SlotState, iframe: HTMLIFrameElement, onChange: () => void) {
  const body = readCreativeBody(iframe);
  if (!body) {
    return;
  }
  const watched = state.creativeWatches.get(iframe);
  if (watched?.document === body.ownerDocument) {
    return;
  }
  watched?.observer.disconnect();

  const observer = new MutationObserver(onChange);
  observer.observe(body, { childList: true, subtree: true });
  state.creativeWatches.set(iframe, { document: body.ownerDocument, observer });
}

function watchCreative(state: SlotState, iframe: HTMLIFrameElement, onChange: () => void) {
  watchCreativeDocument(state, iframe, onChange);
  if (state.watchedIframes.has(iframe)) {
    return;
  }
  state.watchedIframes.add(iframe);

  // A `load` replaces the frame's document, so the new one needs watching too.
  const onLoad = () => {
    watchCreativeDocument(state, iframe, onChange);
    onChange();
  };
  iframe.addEventListener('load', onLoad);
  state.listeners.push([iframe, onLoad]);
}

// The creative iframe is often injected a beat after the slot itself, so this runs again on
// every slot mutation rather than once.
function watchCreatives(slot: HTMLElement, state: SlotState, onChange: () => void) {
  for (const iframe of creativeFrames(slot)) {
    watchCreative(state, iframe, onChange);
  }
}

/** Watches every GPT slot on the page for as long as the guard is mounted. */
function createInterstitialGuard() {
  const slots = new Map<HTMLElement, SlotState>();

  const forget = (slot: HTMLElement, state: SlotState) => {
    releaseSlot(state);
    slots.delete(slot);
  };

  /**
   * The verdict for one slot: an empty interstitial that is painting over the page is taken out
   * of the DOM entirely.
   *
   * Removal rather than restyling, because the styling is not ours to win. GPT writes the slot's
   * geometry inline with `!important` and rewrites it whenever `fullscreen_api_adapter` touches
   * the interstitial, so any override we set is one vendor restyle away from being undone —
   * silently, and only for the visitors unlucky enough to hit the timing. A slot that is no
   * longer in the document has nothing left to restyle.
   */
  const syncSlot = (slot: HTMLElement, state: SlotState) => {
    state.timer = null;
    state.waitingSince = null;

    if (!slot.isConnected || !isSlotRendered(slot) || !isSlotEmpty(slot)) {
      return;
    }
    forget(slot, state);
    slot.remove();
  };

  const scheduleSlotSync = (slot: HTMLElement, state: SlotState) => {
    if (state.timer !== null) {
      clearTimeout(state.timer);
    }
    state.waitingSince ??= Date.now();

    if (Date.now() - state.waitingSince >= EMPTY_SLOT_MAX_WAIT_MS) {
      syncSlot(slot, state);
      return;
    }
    state.timer = setTimeout(() => syncSlot(slot, state), graceFor(slot));
  };

  const onSlotMutated = (slot: HTMLElement, state: SlotState) => {
    watchCreatives(slot, state, () => scheduleSlotSync(slot, state));
    scheduleSlotSync(slot, state);
  };

  const guardSlot = (slot: HTMLElement) => {
    if (slots.has(slot)) {
      return;
    }
    const state = createSlotState();
    slots.set(slot, state);

    // Two observers rather than one. childList has to reach the whole subtree to catch the
    // creative arriving, but the attribute watch must not: GPT's `fullscreen_api_adapter`
    // animates the interstitial's wrapper and iframe through inline style, and watching those
    // would restart the debounce on every frame of that animation.
    const contentObserver = new MutationObserver(() => onSlotMutated(slot, state));
    contentObserver.observe(slot, { childList: true, subtree: true });

    // `class` alongside `style`, because `isSlotRendered` reads *computed* style: the vendor
    // unparking the slot through a class rather than an inline write would otherwise never be
    // re-examined, and a parked slot's verdict leaves nothing scheduled to come back to it.
    const attributeObserver = new MutationObserver(() => onSlotMutated(slot, state));
    attributeObserver.observe(slot, { attributes: true, attributeFilter: ['style', 'class'] });

    state.observers.push(contentObserver, attributeObserver);

    onSlotMutated(slot, state);
  };

  /**
   * Drops slots the vendor has removed from the page. Without this, an app-lifetime component
   * would hold every interstitial ever shown — and its detached DOM — for the whole session.
   */
  const releaseDetachedSlots = () => {
    for (const [slot, state] of slots) {
      if (!slot.isConnected) {
        forget(slot, state);
      }
    }
  };

  const guardAddedNode = (node: Node) => {
    if (!(node instanceof HTMLElement)) {
      return;
    }
    if (node.matches(SLOT_SELECTOR)) {
      guardSlot(node);
    }
    // GPT appends the slot to the document element itself, so the descendant scan is only for
    // the unusual case of it arriving inside a wrapper. Skipping leaves keeps the common case —
    // a React commit adding many small nodes — at one `matches()` call each.
    if (!node.firstElementChild) {
      return;
    }
    for (const nested of node.querySelectorAll<HTMLElement>(SLOT_SELECTOR)) {
      guardSlot(nested);
    }
  };

  // Only the nodes that actually appeared are inspected. A full-document `querySelectorAll` per
  // mutation batch would walk the whole tree on every React commit, on every page, for every
  // visitor — including the tenants that never load GPT at all.
  const onDocumentMutated = (records: MutationRecord[]) => {
    releaseDetachedSlots();
    for (const record of records) {
      for (const node of record.addedNodes) {
        guardAddedNode(node);
      }
    }
  };

  const documentObserver = new MutationObserver(onDocumentMutated);

  const start = () => {
    // GPT displays the interstitial on `DOMContentLoaded` and again on later navigations, so the
    // slot can predate this guard or arrive long after it.
    for (const slot of document.querySelectorAll<HTMLElement>(SLOT_SELECTOR)) {
      guardSlot(slot);
    }
    // `documentElement`, not `document.body`. GPT appends the interstitial as a sibling of
    // `<body>`, directly under `<html>` — a body-scoped observer never sees it arrive, and the
    // one-shot scan above runs at hydration, long before the vendor creates it. That gap is why
    // the first version of this guard did nothing in production while passing its own tests.
    documentObserver.observe(document.documentElement, { childList: true, subtree: true });
  };

  const stop = () => {
    documentObserver.disconnect();
    slots.forEach(releaseSlot);
    slots.clear();
  };

  return { start, stop };
}

/**
 * Removes GPT interstitial slots that cover the page while rendering nothing.
 *
 * The interstitial is a `position: fixed`, `100vw × 100vh`, `z-index: 2147483647 !important`
 * overlay that GPT appends directly to `<html>`. Roughly one impression in ten arrives with an
 * empty creative body, and GPT leaves the overlay in place regardless — with no creative there
 * is no close button either, so the page keeps an invisible sheet over it that swallows every
 * click until the next navigation.
 *
 * Each slot is watched continuously rather than checked once, so a creative that is merely slow
 * is left to arrive, and a slot the vendor unparks later is judged when it is actually shown.
 */
export function useInterstitialAdGuard() {
  useEffect(() => {
    if (typeof MutationObserver === 'undefined') {
      return;
    }

    const guard = createInterstitialGuard();
    guard.start();
    return guard.stop;
  }, []);
}
